import React, { useState, useEffect } from 'react';
import Loading from '../../Common/Loading';

interface EL_AddORModifyTipProps {
    tipType: 'GeneralTip' | 'AlternateTip';
    action: 'A' | 'R' | 'G'; // A = Add Alt, R = Modify Alt, G = Modify Gen
    uniqueId: number | null;
    country: string;
    onClose: (shouldRefresh?: boolean) => void;
}

const EL_AddORModifyTip: React.FC<EL_AddORModifyTipProps> = ({ tipType, action, uniqueId, country, onClose }) => {
    
  const API_BASE =
    process.env.REACT_APP_DOCUMENT_LINK;

    // --- State ---
    const [weekNo, setWeekNo] = useState<string>('');
    const [weekEnding, setWeekEnding] = useState<string>('');
    const [tipText, setTipText] = useState<string>('');
    const [linkText, setLinkText] = useState<string>('');
    const [url, setUrl] = useState<string>('');
    const [selectedGrades, setSelectedGrades] = useState<number[]>([]);
    
    const [availableWeeks, setAvailableWeeks] = useState<any[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [errorMsg, setErrorMsg] = useState<string>('');

    document.body.style.backgroundColor="#fff"

    // Calculate Title
    let title = "";
    if (tipType === 'AlternateTip') {
        title = action === 'A' ? "Add New Alternative Tip of the Week" : "Modify Existing Alternative Tip of the Week";
    } else {
        title = "Modify Existing General Tip of the Week";
    }

    useEffect(() => {
        fetchDetails();
    }, []);

    const fetchDetails = async () => {
        setLoading(true);
        try {
            // Note: action 'G' is treated as 'R' (Modify) in the backend logic
            const ardParam = action === 'G' ? 'R' : action; 
            const reqUrl = `${API_BASE}api/weekly_tips/tip_details?TipType=${tipType}&ARD=${ardParam}&Country=${country}&UniqueID=${uniqueId || ''}`;
            
            const res = await fetch(reqUrl);
            const data = await res.json();

            if (data.error) throw new Error(data.error);

            if (data.availableWeeks) setAvailableWeeks(data.availableWeeks);
            if (data.weekNo) setWeekNo(data.weekNo.toString());
            if (data.weekEnding) setWeekEnding(data.weekEnding);
            if (data.tipText) setTipText(data.tipText);
            if (data.linkText) setLinkText(data.linkText);
            if (data.url) setUrl(data.url);
            if (data.gradeYears) setSelectedGrades(data.gradeYears);

        } catch (err) {
            setErrorMsg("Failed to load tip details.");
        } finally {
            setLoading(false);
        }
    };

    const handleWeekDropdownChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const selectedWk = e.target.value;
        setWeekNo(selectedWk);
        const weekData = availableWeeks.find(w => w.WeekNo.toString() === selectedWk);
        if (weekData) {
            setWeekEnding(weekData.WeekEndDate);
        } else {
            setWeekEnding('');
        }
    };

    const handleGradeToggle = (grade: number) => {
        setSelectedGrades(prev => 
            prev.includes(grade) ? prev.filter(g => g !== grade) : [...prev, grade]
        );
    };

    const handleSave = async () => {
        setErrorMsg('');

        // 1. Basic Validation
        if (tipType === 'AlternateTip') {
            if (action === 'A' && !weekNo) {
                setErrorMsg("Please select Weekno.");
                return;
            }
            if (selectedGrades.length === 0) {
                setErrorMsg("Please select at least one Grade / Year.");
                return;
            }
        }

        setLoading(true);

        // 2. Deep Validation: Check if Alternate Tip already exists for selected grades
        if (tipType === 'AlternateTip') {
            try {
                const checkUrl = `${API_BASE}api/weekly_tips/check_grades?Country=${country}&WeekNo=${weekNo}&UniqueID=${uniqueId || 'null'}`;
                const checkRes = await fetch(checkUrl);
                const checkData = await checkRes.json();

                if (checkData.error) throw new Error(checkData.error);

                const dbGrades: string[] = checkData.used_grades || [];
                const conflict = selectedGrades.some(g => dbGrades.includes(g.toString()));

                if (conflict) {
                    setErrorMsg("Alternative tip already exists for the Grade/Year level.");
                    setLoading(false);
                    return;
                }
            } catch (err) {
                setErrorMsg("Validation failed.");
                setLoading(false);
                return;
            }
        }

        // 3. Save
        try {
            const payload = {
                ARD: action === 'G' ? 'R' : action,
                TipType: tipType,
                Country: country,
                UniqueID: uniqueId,
                weekNo: weekNo,
                weekEndDate: weekEnding,
                tipText: tipText,
                linkText: linkText,
                url: url,
                gradeYears: selectedGrades
            };

            const res = await fetch(`${API_BASE}api/weekly_tips/save_tip`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            const data = await res.json();

            if (data.error) {
                setErrorMsg(data.error);
            } else {
                onClose(true); // Close and refresh parent
            }
        } catch (err) {
            setErrorMsg("An error occurred while saving.");
        } finally {
            setLoading(false);
        }
    };

    // --- Render Helpers ---
    const renderGradeCheckboxes = () => {
        const grades = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
        return (
            <div style={{ fontFamily: 'Courier New' }}>
                {grades.map(g => (
                    <React.Fragment key={g}>
                        <label style={{ marginRight: g > 9 ? '10px' : '15px', cursor: 'pointer' }}>
                            <input 
                                type="checkbox" 
                                value={g} 
                                checked={selectedGrades.includes(g)}
                                onChange={() => handleGradeToggle(g)}
                            />
                            {g}
                        </label>
                        {g === 6 && <><br /><br /></>}
                    </React.Fragment>
                ))}
            </div>
        );
    };

    const styles = {
        overlay: {
            position: 'fixed' as const, top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)', zIndex: 1000,
            display: 'flex', justifyContent: 'center', alignItems: 'center'
        },
        popupWrapper: {
            width: "500px", 
            backgroundColor: "#E7E8FD", // The specific light purple from the legacy code
            boxShadow: "0px 0px 12px rgba(0,0,0,0.5)", 
            padding: "10px",
            border: "1px solid #333"
        },
        table: { fontSize: "11pt", fontFamily: "Arial", width: "100%", paddingLeft: "10px", borderCollapse: 'collapse' as const },
        headerRow: { backgroundColor: "#800000", color: "White", fontWeight: "bold", fontSize: "12pt", textAlign: 'center' as const, padding: '8px' },
        tdLabel: { width: '30%', padding: '8px' },
        tdInput: { width: '70%', padding: '8px' },
        inputField: { border: 'solid 2px #5F895F', width: '100%', boxSizing: 'border-box' as const, padding: '3px' },
        btn: { width: '95px', cursor: 'pointer', padding: '4px', margin: '0 5px' }
    };

    return (
        <div style={styles.overlay}>
            <div style={styles.popupWrapper}>
                {loading && <Loading />}

                <table style={styles.table}>
                    <tbody>
                        <tr>
                            <td colSpan={2} style={styles.headerRow}>
                                {title}
                            </td>
                        </tr>

                        <tr>
                            <td style={styles.tdLabel}>Week No.</td>
                            <td style={styles.tdInput}>
                                {tipType === 'GeneralTip' ? (
                                    <span>{weekNo}</span>
                                ) : (
                                    <select 
                                        value={weekNo} 
                                        onChange={handleWeekDropdownChange}
                                        disabled={action === 'R'} // Cannot change week of existing alt tip
                                        style={{ width: '100%', padding: '3px' }}
                                    >
                                        <option value="">-- Select week number --</option>
                                        {action === 'R' && <option value={weekNo}>{weekNo}</option>}
                                        {action === 'A' && availableWeeks.map(w => (
                                            <option key={w.WeekNo} value={w.WeekNo}>{w.WeekNo}</option>
                                        ))}
                                    </select>
                                )}
                            </td>
                        </tr>

                        <tr>
                            <td style={styles.tdLabel}>Week Ending</td>
                            <td style={styles.tdInput}>
                                <span>{weekEnding}</span>
                            </td>
                        </tr>

                        {tipType === 'AlternateTip' && (
                            <tr>
                                <td style={{ ...styles.tdLabel, verticalAlign: 'top' }}>Grade / Year</td>
                                <td style={styles.tdInput}>
                                    {renderGradeCheckboxes()}
                                </td>
                            </tr>
                        )}

                        <tr>
                            <td style={{ ...styles.tdLabel, verticalAlign: 'top' }}>Tip</td>
                            <td style={styles.tdInput}>
                                <textarea 
                                    style={{ ...styles.inputField, height: '95px', resize: 'vertical' }} 
                                    value={tipText}
                                    onChange={e => setTipText(e.target.value)}
                                />
                            </td>
                        </tr>

                        <tr>
                            <td style={styles.tdLabel}>Link Text</td>
                            <td style={styles.tdInput}>
                                <input 
                                    type="text" 
                                    maxLength={200}
                                    style={styles.inputField} 
                                    value={linkText}
                                    onChange={e => setLinkText(e.target.value)}
                                />
                            </td>
                        </tr>

                        <tr>
                            <td style={styles.tdLabel}>URL</td>
                            <td style={styles.tdInput}>
                                <input 
                                    type="text" 
                                    maxLength={200}
                                    style={styles.inputField} 
                                    value={url}
                                    onChange={e => setUrl(e.target.value)}
                                />
                            </td>
                        </tr>

                        {errorMsg && (
                            <tr>
                                <td colSpan={2} style={{ color: 'red', textAlign: 'center', fontWeight: 'bold' }}>
                                    {errorMsg}
                                </td>
                            </tr>
                        )}

                        <tr>
                            <td colSpan={2} style={{ textAlign: 'center', paddingTop: '15px' }}>
                                <button style={styles.btn} onClick={handleSave}>Save</button>
                                <button style={styles.btn} onClick={() => onClose(false)}>Cancel</button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default EL_AddORModifyTip;